/*
 * Thunderline – first-party attribution + tiny event layer (v3, 2026-10-21: generate_lead + form_name/value/currency; v2 2026-10-20: + cart consent attributes)
 * - Captures gclid / gbraid / wbraid and utm_* from the landing URL.
 *   Nothing is stored until Shopify's Customer Privacy API reports consent:
 *     click IDs (gclid/gbraid/wbraid) need MARKETING consent,
 *     utm_* need ANALYTICS or MARKETING consent.
 *   If consent is withdrawn, the stored values are removed.
 * - Adds the stored values as hidden fields to every Shopify contact form
 *   (contact[gclid] …) and to the cart form (attributes[_gclid] …) so they
 *   reach the lead e-mail / the order for offline conversion upload.
 * - Publishes custom events for a later GA4/Ads setup (custom pixel / app):
 *     generate_lead  – after a contact/quote form was posted successfully
 *     tel_click      – click on tel:+37258326609
 *     quote_start / mailto_click / chat_start – 2026-10-22 (CMO audit F1, see block above boot)
 *   via Shopify.analytics.publish(name, data) and window.dataLayer.push().
 *   No Google tag / gtag is loaded here.
 * Debug: add ?tl_debug=1 to any URL (logs to console).
 */
(function () {
  'use strict';
  if (window.__tlAttr) return;
  window.__tlAttr = true;

  var KEY = 'tl_attr_v1';
  var LEAD_KEY = 'tl_lead_pending_v1';
  var EC_KEY = 'tl_lead_ec_v1'; // 2026-10-22 v7 enhanced conversions: SHA-256 hashes only, pixel-only (see ecData)
  var TTL = 90 * 24 * 60 * 60 * 1000; // 90 days (Google Ads click window)
  var PHONE = '+37258326609';
  var CLICK = ['gclid', 'gbraid', 'wbraid'];
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var DEBUG = /[?&]tl_debug=1/.test(location.search);

  function log() { if (DEBUG && window.console) console.log.apply(console, ['[tl-attr]'].concat([].slice.call(arguments))); }

  window.dataLayer = window.dataLayer || [];

  /* ---------- URL params (kept in memory only until consent) ---------- */
  function urlParams() {
    var out = {}, any = false, sp;
    try { sp = new URLSearchParams(location.search); } catch (e) { return null; }
    CLICK.concat(UTM).forEach(function (k) {
      var v = sp.get(k);
      if (v) { out[k] = String(v).slice(0, 255); any = true; }
    });
    return any ? out : null;
  }
  var landing = urlParams();
  if (landing) landing._landing = location.pathname.slice(0, 255);

  /* ---------- consent ---------- */
  function cp() { return window.Shopify && window.Shopify.customerPrivacy; }
  function allowed(fn) {
    var c = cp();
    try { return !!(c && typeof c[fn] === 'function' && c[fn]()); } catch (e) { return false; }
  }
  function marketingOk() { return allowed('marketingAllowed'); }
  function allowedReady() { var c = cp(); return !!(c && typeof c.marketingAllowed === 'function'); } // v7
  function analyticsOk() { return allowed('analyticsProcessingAllowed'); }

  /* ---------- storage ---------- */
  function read() {
    try {
      var r = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!r) return null;
      if (!r.ts || Date.now() - r.ts > TTL) { localStorage.removeItem(KEY); return null; }
      return r;
    } catch (e) { return null; }
  }
  function write(r) { try { localStorage.setItem(KEY, JSON.stringify(r)); } catch (e) {} }
  function clearAll() { try { localStorage.removeItem(KEY); } catch (e) {} }

  // Enforce current consent on what is stored, then store a new touch if allowed.
  function sync() {
    var m = marketingOk(), a = analyticsOk();
    var cur = read();
    if (cur) {
      if (!m && !a) { clearAll(); cur = null; log('consent gone -> cleared'); }
      else if (!m) {
        var changed = false;
        CLICK.forEach(function (k) { if (cur[k]) { delete cur[k]; changed = true; } });
        if (changed) { write(cur); log('marketing denied -> click ids removed'); }
      }
    }
    if (landing && (m || a)) {
      var rec = { ts: Date.now(), landing_page: landing._landing };
      var useful = false;
      if (m) CLICK.forEach(function (k) { if (landing[k]) { rec[k] = landing[k]; useful = true; } });
      UTM.forEach(function (k) { if (landing[k]) { rec[k] = landing[k]; useful = true; } });
      // keep `landing` for this page view: if marketing consent comes later on the same page,
      // the click IDs are added on the next sync.
      var prev = read();
      var same = prev && JSON.stringify(Object.assign({}, prev, { ts: 0 })) === JSON.stringify(Object.assign({}, rec, { ts: 0 }));
      if (useful && !same) { write(rec); log('stored', rec); }
    }
    fill();
    cartSync();
  }

  /* ---------- cart attributes (2026-10-21 ads-conversions; r2: no cart writes before there is something to write) ----------
   * Keeps the cart's consent attributes current via /cart/update.js:
   * tl_consent_ads = granted only with marketing consent (ad_storage + ad_user_data granted), else denied;
   * without marketing consent the click-ID attributes are emptied (removed).
   * r2: writes ONLY when the cart has items, or the cart holds stale values that must be cleared, or the status /
   * click ID changed since the last sync of this tab (sessionStorage tl_cart_sync_v1). An empty cart is never
   * written just to say "denied"; once items are added (theme cart-update event or next page view) it is synced. */
  var cartBusy = false, SYNC_KEY = 'tl_cart_sync_v1';
  function lastSync() { try { return sessionStorage.getItem(SYNC_KEY); } catch (e) { return null; } }
  function setSync(v) { try { sessionStorage.setItem(SYNC_KEY, v); } catch (e) {} }
  function cartSync(force) {
    if (cartBusy || window.top !== window.self || !window.fetch) return;
    var m = marketingOk(), rec = read() || {};
    var want = { tl_consent_ads: m ? 'granted' : 'denied', _tl_consent_marketing: m ? 'granted' : 'denied', _tl_consent_analytics: analyticsOk() ? 'granted' : 'denied' };
    CLICK.forEach(function (k) { want['_' + k] = m ? (rec[k] || null) : ''; });
    var sig = JSON.stringify(want), last = lastSync();
    if (!force && last === sig) return; // already synced in this tab
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    cartBusy = true;
    fetch(root + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      var a = (cart && cart.attributes) || {}, diff = {}, any = false, stale = false;
      for (var k in want) {
        if (want[k] === null) continue; // consent granted but nothing new stored: keep what the cart has
        var cur = a[k] == null ? '' : String(a[k]);
        if (cur !== want[k]) { diff[k] = want[k]; any = true; if (cur !== '' && want[k] === '') stale = true; }
      }
      if (!any) { setSync(sig); return; }
      var items = (cart && cart.item_count) || 0;
      var changed = last !== null && last !== sig; // status or click ID changed since the last sync in this tab
      if (!items && !stale && !changed) { log('cart empty, nothing to clear -> no write'); return; }
      log('cart attributes ->', diff);
      return fetch(root + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ attributes: diff }) })
        .then(function (r) { if (r.ok) setSync(sig); });
    }).catch(function (e) { log('cart sync failed', e); }).then(function () { cartBusy = false; });
  }
  // r2: sync as soon as something is added to the cart (Dawn pubsub, assets/pubsub.js + constants.js)
  function hookCart() {
    try {
      // PUB_SUB_EVENTS is a top-level const in constants.js (global lexical scope, not a window property)
      var ev = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
      if (typeof window.subscribe === 'function') {
        window.subscribe(ev, function () { setTimeout(function () { if (cp()) cartSync(true); }, 300); });
      }
    } catch (e) {}
  }

  /* ---------- hidden fields ---------- */
  var FIELD_CLASS = 'tl-attr-field';
  function fieldsFor(rec, kind) {
    var out = [];
    if (!rec) return out;
    var m = marketingOk(), a = analyticsOk();
    if (!m && !a) return out;
    var keys = (m ? CLICK : []).concat(UTM);
    keys.forEach(function (k) {
      if (!rec[k]) return;
      out.push([kind === 'cart' ? 'attributes[_' + k + ']' : 'contact[' + k + ']', rec[k]]);
    });
    if (out.length && rec.landing_page) {
      out.push([kind === 'cart' ? 'attributes[_landing_page]' : 'contact[landing_page]', rec.landing_page]);
    }
    return out;
  }
  function isContactForm(f) {
    var act = (f.getAttribute('action') || '');
    return /\/contact(\b|#|$|\?)/.test(act) && f.querySelector('input[name="form_type"][value="contact"]');
  }
  function isCartForm(f) {
    return f.id === 'cart' || f.id === 'CartDrawer-Form';
  }
  function fillForm(f, kind) {
    [].slice.call(f.querySelectorAll('input.' + FIELD_CLASS)).forEach(function (el) { el.parentNode.removeChild(el); });
    var list = fieldsFor(read(), kind);
    if (kind === 'cart') {
      // Consent state at the time of purchase (no personal data). The offline export only
      // hashes e-mail/phone when _tl_consent_marketing = granted.
      list.push(['attributes[_tl_consent_marketing]', marketingOk() ? 'granted' : 'denied']);
      list.push(['attributes[_tl_consent_analytics]', analyticsOk() ? 'granted' : 'denied']);
      // 2026-10-21 ads-conversions: tl_consent_ads (offline export key); without marketing consent the click IDs are blanked.
      list.push(['attributes[tl_consent_ads]', marketingOk() ? 'granted' : 'denied']);
      if (!marketingOk()) CLICK.forEach(function (k) { list.push(['attributes[_' + k + ']', '']); });
    }
    list.forEach(function (p) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = p[0]; i.value = p[1]; i.className = FIELD_CLASS;
      f.appendChild(i);
    });
  }
  function fill() {
    [].slice.call(document.forms).forEach(function (f) {
      if (isContactForm(f)) fillForm(f, 'contact');
      else if (isCartForm(f)) fillForm(f, 'cart');
    });
  }

  /* ---------- event layer ---------- */
  function publish(name, data, tries) {
    tries = tries || 0;
    var a = window.Shopify && window.Shopify.analytics;
    if (a && typeof a.publish === 'function') {
      try { a.publish(name, data); log('published', name, data); } catch (e) { log('publish failed', e); }
    } else if (tries < 20) {
      setTimeout(function () { publish(name, data, tries + 1); }, 250);
    }
  }
  // v7: pixelOnly = extra fields for the Shopify.analytics.publish payload ONLY (never dataLayer, never the tl:* DOM event)
  function emit(name, data, pixelOnly) {
    data = data || {};
    data.lang = (document.documentElement.getAttribute('lang') || '').slice(0, 10);
    data.page_path = location.pathname;
    var dl = { event: name };
    for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) dl[k] = data[k];
    window.dataLayer.push(dl);
    // 2026-10-21 SEO step 5: tl_ads_snippet tells the custom pixel whether the old theme snippet (tl-google-ads) still sends the Ads conversion
    var pub = {}; for (var p in data) if (Object.prototype.hasOwnProperty.call(data, p)) pub[p] = data[p];
    pub.tl_ads_snippet = !!window.__tlAds;
    if (pixelOnly) for (var q in pixelOnly) if (Object.prototype.hasOwnProperty.call(pixelOnly, q)) pub[q] = pixelOnly[q];
    publish(name, pub);
    // 2026-10-21 ads-conversions: DOM hook for snippets/tl-google-ads.liquid (Ads conversions, consent-gated there)
    try { window.dispatchEvent(new CustomEvent('tl:' + name, { detail: data })); } catch (e) {}
  }

  function val(f, name) {
    var el = f.querySelector('[name="' + name + '"]');
    return el && el.value ? String(el.value).slice(0, 200) : '';
  }
  // No personal data (name, e-mail, phone, VAT number, message) goes into the event.
  function leadPayload(f) {
    var ds = f.dataset || {};
    var price = ds.tlPrice ? Number(ds.tlPrice) : undefined;
    var qty = Number(val(f, 'contact[Quantity]')) || 1;
    return {
      form: val(f, 'contact[Form]') || f.id || 'contact',
      form_name: val(f, 'contact[Form]') || f.id || 'contact',
      form_id: f.id || '',
      value: price ? Math.round(price * qty * 100) / 100 : undefined,
      product: ds.tlProduct || val(f, 'contact[Product]') || val(f, 'contact[Product type]'),
      sku: ds.tlSku || val(f, 'contact[SKU]'),
      price_ex_vat: ds.tlPrice ? Number(ds.tlPrice) : undefined,
      currency: ds.tlCurrency || undefined,
      carrier_model: val(f, 'contact[Excavator make and model]') || val(f, 'contact[Machine]') || val(f, 'contact[Carrier machine brand and model]') || (val(f, 'contact[Carrier make]') + ' ' + val(f, 'contact[Carrier model]')).trim(), // 2026-10-21 quote-form v3: one excavator field (v2 make + model kept as fallback)
      tonnage: val(f, 'contact[Carrier weight (t)]'),
      country: val(f, 'contact[Country]')
    };
  }

  /* ---------- v7 (2026-10-22): enhanced conversions for the Google Ads lead conversion ----------
     Only with marketing consent (checked at submit AND when the lead is published). E-mail (trim + lowercase) and,
     if filled, phone (E.164 when it is simple, else trimmed) are SHA-256-hashed HERE, synchronously, so no plain
     e-mail/phone is ever stored (sessionStorage holds only the hex hashes) or published. The hashes go only into
     the Shopify.analytics.publish('generate_lead') payload for the custom pixel (gtag user_data before the Ads lead
     conversion). Never dataLayer, never GA4, never the tl:generate_lead DOM event. */
  function sha256(str) {
    function rr(x, n) { return (x >>> n) | (x << (32 - n)); }
    var K = [], H = [], i, j, pr = 0, c = 2;
    function frac(x) { return ((x - Math.floor(x)) * 4294967296) | 0; }
    while (pr < 64) { for (j = 2; j * j <= c; j++) if (c % j === 0) break; if (j * j > c) { if (pr < 8) H[pr] = frac(Math.sqrt(c)); K[pr++] = frac(Math.cbrt(c)); } c++; }
    var b = unescape(encodeURIComponent(str)), w = [], l = b.length * 8;
    for (i = 0; i < b.length; i++) w[i >> 2] |= b.charCodeAt(i) << (24 - (i % 4) * 8);
    w[l >> 5] |= 0x80 << (24 - l % 32); w[(((l + 64) >> 9) << 4) + 15] = l;
    for (i = 0; i < w.length; i += 16) {
      var W = [], a = H.slice(0);
      for (j = 0; j < 64; j++) {
        if (j < 16) W[j] = w[i + j] | 0;
        else W[j] = (W[j - 16] + (rr(W[j - 15], 7) ^ rr(W[j - 15], 18) ^ (W[j - 15] >>> 3)) + W[j - 7] + (rr(W[j - 2], 17) ^ rr(W[j - 2], 19) ^ (W[j - 2] >>> 10))) | 0;
        var t1 = (a[7] + (rr(a[4], 6) ^ rr(a[4], 11) ^ rr(a[4], 25)) + ((a[4] & a[5]) ^ (~a[4] & a[6])) + K[j] + W[j]) | 0;
        var t2 = ((rr(a[0], 2) ^ rr(a[0], 13) ^ rr(a[0], 22)) + ((a[0] & a[1]) ^ (a[0] & a[2]) ^ (a[1] & a[2]))) | 0;
        a = [(t1 + t2) | 0].concat(a.slice(0, 7)); a[4] = (a[4] + t1) | 0;
      }
      for (j = 0; j < 8; j++) H[j] = (H[j] + a[j]) | 0;
    }
    var out = ''; for (i = 0; i < 8; i++) out += ('00000000' + (H[i] >>> 0).toString(16)).slice(-8);
    return out;
  }
  function normEmail(v) {
    v = String(v || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return '';
    var at = v.lastIndexOf('@'), dom = v.slice(at + 1);
    return (dom === 'gmail.com' || dom === 'googlemail.com') ? v.slice(0, at).replace(/\./g, '') + v.slice(at) : v; // Google's normalisation
  }
  function normPhone(v) {
    v = String(v || '').trim(); if (!v) return '';
    var d = v.replace(/\(0\)/g, '').replace(/[\s().\-\/]/g, ''); // '+49 (0)171 ...' -> +49171...
    if (/^00[1-9]\d{6,14}$/.test(d)) d = '+' + d.slice(2);
    return /^\+[1-9]\d{6,14}$/.test(d) ? d : v; // E.164 if simple, else trimmed as typed
  }
  function ecData(f) {
    if (!marketingOk()) return null;
    var em = normEmail(val(f, 'contact[email]'));
    if (!em) return null;
    var telEl = f.querySelector('[name="contact[Phone]"]') || f.querySelector('input[type="tel"]');
    var ph = normPhone(telEl && telEl.value ? String(telEl.value).slice(0, 40) : '');
    var o = { em: sha256(em) };
    if (ph) o.ph = sha256(ph);
    return o;
  }

  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') return;
    if (isContactForm(f)) {
      fillForm(f, 'contact');
      try { sessionStorage.setItem(LEAD_KEY, JSON.stringify(leadPayload(f))); } catch (err) {}
      try { var ec = ecData(f); if (ec) sessionStorage.setItem(EC_KEY, JSON.stringify(ec)); else sessionStorage.removeItem(EC_KEY); } catch (err) {}
    } else if (isCartForm(f)) {
      fillForm(f, 'cart');
    }
  }, true);

  function checkLeadSuccess() {
    if (!/[?&]contact_posted=true/.test(location.search)) return;
    // 2026-10-21 ads-conversions: the chat hand-off posts in a hidden iframe; the chat itself reports that lead (tl:lead_submitted)
    if (window.top !== window.self) { try { sessionStorage.removeItem(LEAD_KEY); sessionStorage.removeItem(EC_KEY); } catch (e) {} return; }
    var payload = null, ec = null;
    try {
      payload = JSON.parse(sessionStorage.getItem(LEAD_KEY) || 'null');
      sessionStorage.removeItem(LEAD_KEY);
    } catch (e) {}
    try { ec = JSON.parse(sessionStorage.getItem(EC_KEY) || 'null'); sessionStorage.removeItem(EC_KEY); } catch (e) {}
    // only count it if this tab really submitted a contact form (Shopify shows the success
    // message for any URL with contact_posted=true, so the message alone is not proof)
    if (!payload) return;
    // strip the flag so a reload does not count the lead twice
    try {
      var u = new URL(location.href);
      u.searchParams.delete('contact_posted');
      history.replaceState(history.state, '', u.pathname + (u.search || '') + u.hash);
    } catch (e) {}
    // v7: hashed e-mail/phone -> publish payload only (pixel), and only if marketing consent is still granted.
    // The consent API may not be loaded yet at boot, so with hashes waiting we wait for it (max ~4 s) before emitting.
    var valid = ec && ec.em && /^[0-9a-f]{64}$/.test(ec.em);
    function go() {
      var px = null;
      if (valid && marketingOk()) {
        px = { tl_ec: { sha256_email_address: ec.em } };
        if (ec.ph && /^[0-9a-f]{64}$/.test(ec.ph)) px.tl_ec.sha256_phone_number = ec.ph;
      }
      emit('generate_lead', payload, px);
    }
    if (!valid || allowedReady()) { go(); return; }
    var n = 0, t = setInterval(function () { if (allowedReady() || ++n >= 40) { clearInterval(t); go(); } }, 100);
    // 2026-10-21 ads-conversions r2: the de-marketing gtag('event','generate_lead') without send_to was removed. The ONE Ads conversion per
    // lead is sent by snippets/tl-google-ads.liquid (send_to AW-18527391897/7y-sCM6t2qMdEJmhxoJF, on tl:generate_lead).
  }

  // 2026-10-21 SEO step 5: chat hand-off lead (sections/tl-chat.liquid dispatches tl:lead_submitted) -> also published for the custom pixel
  // (publish only: no dataLayer push / no tl:generate_lead DOM event, the old snippet handles tl:lead_submitted itself while it exists)
  window.addEventListener('tl:lead_submitted', function (e) {
    if (window.top !== window.self) return;
    var d = (e && e.detail) || {};
    publish('generate_lead', { form: d.form || 'chat', form_name: d.form || 'chat', lead_type: d.type || '', lang: (document.documentElement.getAttribute('lang') || '').slice(0, 10), page_path: location.pathname, tl_ads_snippet: !!window.__tlAds });
  });

  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="tel:"]') : null;
    if (!a) return;
    var num = (a.getAttribute('href') || '').replace(/^tel:/i, '').replace(/[^\d+]/g, '');
    if (num === PHONE || num === '00372' + PHONE.slice(4) || num === PHONE.slice(1)) {
      emit('tel_click', { phone: PHONE, link_location: (a.closest('[id]') || {}).id || '' });
    }
  }, true);

  /* ---------- 2026-10-22 CMO tracking audit F1: quote_start / mailto_click / chat_start ----------
   * quote_start  – once per page, first focusin/input/change in #QuoteForm (/pages/request-a-quote),
   *                #TlAttQuoteForm (product pages) or #ContactForm (/pages/contact). Data: form_id, product.
   * mailto_click – click on any a[href^="mailto:"]. Data: link_location (nearest [id]), no address/personal data.
   * chat_start   – first open of the chat widget per browser session (sections/tl-chat.liquid dispatches tl:chat_open).
   * All via emit() -> dataLayer + Shopify.analytics.publish (custom pixel v3.2 forwards them to GA4 + Ads). */
  var QUOTE_FORMS = { QuoteForm: 1, TlAttQuoteForm: 1, ContactForm: 1 };
  var quoteStarted = false;
  function onQuoteInteract(e) {
    if (quoteStarted) return;
    var t = e.target;
    var f = t && (t.form || (t.closest ? t.closest('form') : null));
    if (!f || !QUOTE_FORMS[f.id]) return;
    quoteStarted = true;
    emit('quote_start', { form_id: f.id, product: (f.dataset && f.dataset.tlProduct) || '' });
  }
  document.addEventListener('focusin', onQuoteInteract, true);
  document.addEventListener('input', onQuoteInteract, true);
  document.addEventListener('change', onQuoteInteract, true);

  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href^="mailto:"]') : null;
    if (!a) return;
    emit('mailto_click', { link_location: (a.closest('[id]') || {}).id || '' });
  }, true);

  var CHAT_KEY = 'tl_chat_start_v1';
  window.addEventListener('tl:chat_open', function () {
    if (window.top !== window.self) return;
    try { if (sessionStorage.getItem(CHAT_KEY)) return; sessionStorage.setItem(CHAT_KEY, '1'); } catch (err) {}
    emit('chat_start', {});
  });

  /* ---------- boot ---------- */
  function boot() {
    hookCart(); // 2026-10-21 ads-conversions r2
    checkLeadSuccess();
    fill();
    var loader = window.Shopify && window.Shopify.loadFeatures;
    if (typeof loader === 'function') {
      try {
        loader([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
          if (err) { log('consent api load error', err); return; }
          sync();
        });
      } catch (e) { log(e); }
    } else if (cp()) {
      sync();
    }
    document.addEventListener('visitorConsentCollected', function () { setTimeout(sync, 0); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.tlAttribution = {
    version: 7,
    stored: read,
    consent: function () { return { marketing: marketingOk(), analytics: analyticsOk() }; }
  };
})();
